import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TWRChartBloomberg from '../TWRChartBloomberg';
import EquityChartBloomberg from '../EquityChartBloomberg';
import '../../styles/bloomberg-theme.css';
import '../../styles/ios-theme.css';

type ChartType = 'twr' | 'equity';

interface UnifiedChartsBloombergProps {
  initialPeriod?: string;
  userId?: string;
  isConsolidated?: boolean;
  height?: number;
}

const UnifiedChartsBloomberg: React.FC<UnifiedChartsBloombergProps> = ({
  initialPeriod = '7d',
  userId,
  isConsolidated = false,
  height = 400
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);
  const [activeChart, setActiveChart] = useState<ChartType>('twr');

  const periods = ['24h', '7d', 'mtd', '30d', 'ytd', 'all'];

  const getPeriodLabel = (period: string): string => {
    switch(period) {
      case 'all': return 'All';
      case 'mtd': return 'MTD';
      case 'ytd': return 'YTD';
      case '24h': return '1D';
      case '7d': return '1W';
      case '30d': return '1M';
      default: return period.toUpperCase();
    }
  };

  return (
    <div className="ios-chart-container">
      {/* Header */}
      <div className="ios-chart-header">
        <div>
          <h3 className="ios-chart-title">
            {activeChart === 'twr' ? 'Performance' : 'Portfolio Value'}
          </h3>
          <p style={{
            fontSize: 'var(--ios-text-sm)',
            color: 'var(--ios-label-secondary)',
            margin: 0
          }}>
            {activeChart === 'twr' ? 'Time-weighted returns' : 'Value over time'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 'var(--ios-space-md)', alignItems: 'center' }}>
          {/* Chart Type Toggle - Segmented Control */}
          <div className="ios-segment-control">
            <motion.button
              className={`ios-segment-button ${activeChart === 'twr' ? 'ios-segment-button--active' : ''}`}
              onClick={() => setActiveChart('twr')}
              whileTap={{ scale: 0.98 }}
            >
              TWR
            </motion.button>
            <motion.button
              className={`ios-segment-button ${activeChart === 'equity' ? 'ios-segment-button--active' : ''}`}
              onClick={() => setActiveChart('equity')}
              whileTap={{ scale: 0.98 }}
            >
              Equity
            </motion.button>
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div style={{ marginBottom: 'var(--ios-space-md)' }}>
        {activeChart === 'twr' ? (
          <TWRChartBloomberg
            period={selectedPeriod}
            setPeriod={setSelectedPeriod}
            userId={userId}
            isConsolidated={isConsolidated}
            height={height}
          />
        ) : (
          <EquityChartBloomberg
            period={selectedPeriod}
            userId={userId}
            isConsolidated={isConsolidated}
          />
        )}
      </div>

      {/* Period Selector - Segmented Control Style */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--ios-space-xs)'
      }}>
        <div className="ios-segment-control">
          {periods.map(period => (
            <motion.button
              key={period}
              className={`ios-segment-button ${selectedPeriod === period ? 'ios-segment-button--active' : ''}`}
              onClick={() => setSelectedPeriod(period)}
              whileTap={{ scale: 0.98 }}
            >
              {getPeriodLabel(period)}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UnifiedChartsBloomberg;